import * as React from 'react'

import {ConnectedProps} from 'react-redux'
import {Navigate, Route, Routes} from 'react-router'

import * as coreActions from '../actions/core'
import {State} from '../reducers'

import {removeBootsplash} from '../../../commons/src/services/bootsplash'
import * as dixa from '../../../commons/src/services/dixa'
import * as elevio from '../../../commons/src/services/elevio'
import {connect} from '../../../commons/src/services/loop'
import {anyPermission, anyPermissionExceptOperator} from '../libs/user'
import {RequireAuth, isPermitted} from './utils/routing'

import ROUTES from '../routes'

import ResetPasswordPage from './auth/reset-password-page'
import SignInAdminPage from './auth/sign-in-admin-page'
import SignInPage from './auth/sign-in-page'
import DiscountsPage from './discounts-page'
import CreateDynamicProductionTimePage from './dynamic-production-time/create-dynamic-production-time-page'
import DynamicProductionTimesPage from './dynamic-production-time/dynamic-production-times-page'
import EditDynamicProductionTimePage from './dynamic-production-time/edit-dynamic-production-time-page'
import ForbiddenPage from './forbidden-page'
import InfillsPage from './infills-page'
import InvoicesPage from './invoices/invoices-page'
import Modal from './modals'
import NotFoundPage from './not-found-page'
import OrderDetailPage from './order/order-detail-page'
import OrdersPage from './order/orders-page'
import CreatePricingPage from './pricings/create-pricing-page'
import EditPricingPage from './pricings/edit-pricing-page'
import PricingsPage from './pricings/pricings-page'
import ProductionTimesPage from './production-times-page'
import CreateProviderInfoPage from './provider-info/create-provider-info-page'
import EditProviderInfoPage from './provider-info/edit-provider-info-page'
import ProviderInfoPage from './provider-info/provider-info-page'
import SettingsPage from './settings/settings-page'
import ShippingsPage from './shippings-page'

export const routes = [
  // Order pages
  {
    path: ROUTES.ORDERS,
    component: OrdersPage,
    requireAuth: true,
    requiredPermissions: anyPermission,
  },
  {
    path: `${ROUTES.ORDER_DETAIL}/*`,
    component: OrderDetailPage,
    requireAuth: true,
    requiredPermissions: anyPermission,
  },
  // Invoices
  {
    path: `${ROUTES.INVOICES}/*`,
    component: InvoicesPage,
    requireAuth: true,
    requiredPermissions: anyPermissionExceptOperator,
  },
  // Catalog table pages
  {
    path: ROUTES.PROVIDER_INFOS,
    component: ProviderInfoPage,
    requireAuth: true,
    requiredPermissions: anyPermissionExceptOperator,
  },
  {
    path: ROUTES.CREATE_PROVIDER_INFO,
    component: CreateProviderInfoPage,
    requireAuth: true,
    requiredPermissions: anyPermissionExceptOperator,
  },
  {
    path: `${ROUTES.EDIT_PROVIDER_INFO}/*`,
    component: EditProviderInfoPage,
    requireAuth: true,
    requiredPermissions: anyPermissionExceptOperator,
  },
  {
    path: ROUTES.PRICINGS,
    component: PricingsPage,
    requireAuth: true,
    requiredPermissions: anyPermissionExceptOperator,
  },
  {
    path: ROUTES.CREATE_PRICING,
    component: CreatePricingPage,
    requireAuth: true,
    requiredPermissions: anyPermissionExceptOperator,
  },
  {
    path: ROUTES.EDIT_PRICING,
    component: EditPricingPage,
    requireAuth: true,
    requiredPermissions: anyPermissionExceptOperator,
  },
  {
    path: ROUTES.INFILLS,
    component: InfillsPage,
    requireAuth: true,
    requiredPermissions: anyPermissionExceptOperator,
  },
  {
    path: ROUTES.PRODUCTION_TIMES,
    component: ProductionTimesPage,
    requireAuth: true,
    requiredPermissions: anyPermissionExceptOperator,
  },
  {
    path: ROUTES.DYNAMIC_PRODUCTION_TIMES,
    component: DynamicProductionTimesPage,
    requireAuth: true,
    requiredPermissions: anyPermissionExceptOperator,
  },
  {
    path: ROUTES.CREATE_DYNAMIC_PRODUCTION_TIMES,
    component: CreateDynamicProductionTimePage,
    requireAuth: true,
    requiredPermissions: anyPermissionExceptOperator,
  },
  {
    path: ROUTES.EDIT_DYNAMIC_PRODUCTION_TIMES,
    component: EditDynamicProductionTimePage,
    requireAuth: true,
    requiredPermissions: anyPermissionExceptOperator,
  },
  {
    path: ROUTES.SHIPPINGS,
    component: ShippingsPage,
    requireAuth: true,
    requiredPermissions: anyPermissionExceptOperator,
  },
  {
    path: ROUTES.DISCOUNTS,
    component: DiscountsPage,
    requireAuth: true,
    requiredPermissions: anyPermissionExceptOperator,
  },
  // Account pages
  {
    path: `${ROUTES.SETTINGS}/*`,
    component: SettingsPage,
    requireAuth: true,
    requiredPermissions: anyPermission,
  },
  {
    path: ROUTES.SIGN_IN,
    component: SignInPage,
    requireAuth: false,
  },
  {
    path: ROUTES.SIGN_IN_ADMIN,
    component: SignInAdminPage,
    requireAuth: false,
  },
  {
    path: ROUTES.RESET_PASSWORD,
    component: ResetPasswordPage,
    requireAuth: false,
  },
  // Others
  {
    path: ROUTES.FORBIDDEN,
    component: ForbiddenPage,
    requireAuth: false,
  },
]

const mapStateToProps = (state: State) => ({
  initDone: state.core.initDone && state.auth.initDone,
  authUser: state.auth.user,
  provider: state.core.provider,
})

const mapDispatchToProps = {
  init: coreActions.init,
}

const connector = connect(mapStateToProps, mapDispatchToProps)

type Props = ConnectedProps<typeof connector>

const App = ({init, initDone, authUser, provider}: Props) => {
  React.useEffect(() => {
    init()
  }, [])

  React.useEffect(() => {
    if (initDone) {
      removeBootsplash()
    }
  }, [initDone])

  React.useEffect(() => {
    if (provider && !!authUser) {
      dixa.setUser(provider.name, authUser.attributes.email)
    } else {
      // always hide elevio widget if we have no auth user
      elevio.hide()
    }
  }, [authUser, provider])

  const isAuthenticated = !!authUser && initDone
  const providerExists = !!provider

  return (
    <>
      {initDone && (
        <Routes>
          <Route path="/" element={<Navigate to={ROUTES.ORDERS} />} />
          {routes
            .filter(route => !route.requireAuth)
            .map(route => (
              <Route key="route" path={route.path} element={React.createElement(route.component)} />
            ))}
          {routes
            .filter(route => route.requireAuth)
            .map(route => (
              <Route
                key="route"
                path={route.path}
                element={
                  <RequireAuth
                    isAuthenticated={isAuthenticated}
                    isPermitted={
                      isAuthenticated &&
                      providerExists &&
                      isPermitted(authUser.permission, route.requiredPermissions)
                    }
                  >
                    {React.createElement(route.component)}
                  </RequireAuth>
                }
              />
            ))}
          <Route
            key="route"
            path="*"
            element={
              <RequireAuth isAuthenticated={isAuthenticated} isPermitted>
                <NotFoundPage />
              </RequireAuth>
            }
          />
        </Routes>
      )}
      <Modal />
    </>
  )
}

export default connector(App)
